// Tiny haptic helper — wraps navigator.vibrate (Android) + a Web Vibration
// fallback. iOS Safari does NOT expose navigator.vibrate inside the PWA, but
// *does* honor small taps on form controls. For iOS we trigger a zero-duration
// haptic via a hidden AudioContext "click" which most iPhones render as a
// gentle tap when silent mode is off (the Apple Haptics API is still not
// exposed to the web). This is a best-effort "feel real" cue.
//
// Usage: import tap from "../utils/haptics"; tap("light" | "success" | "error")
//
// All paths are no-ops when the browser / device doesn't support vibration,
// so calling this from any onClick is always safe.

const PATTERNS = {
  light: 12,      // quick single pulse — generic tap
  success: [18, 30, 18], // two quick pulses — captured email / win
  error: [40, 30, 40, 30, 40],
};

const isIOS = () => /iPhone|iPad|iPod/i.test(navigator.userAgent || "");

export default function tap(kind = "light") {
  try {
    // Respect the same mute toggle the app uses for sound cues — if crew
    // muted notifications, they probably also want quiet hardware.
    if (typeof localStorage !== "undefined" && localStorage.getItem("ecDashCueMuted") === "1") return;

    // Android / modern Chrome: native vibration API
    if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function" && !isIOS()) {
      navigator.vibrate(PATTERNS[kind] || PATTERNS.light);
      return;
    }

    // iOS: nudge the system via a very short silent audio blip — the
    // phone's Taptic engine is tied to UI controls, not JS, so we use a
    // subtle tone the user barely perceives but registers as a "tick".
    // Wrapped in a try/catch since AudioContext can be blocked pre-gesture.
    if (isIOS() && typeof window !== "undefined" && (window.AudioContext || window.webkitAudioContext)) {
      const AC = window.AudioContext || window.webkitAudioContext;
      const ctx = tap._ctx || (tap._ctx = new AC());
      if (ctx.state === "suspended") ctx.resume();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      gain.gain.value = 0.0001; // essentially silent — tactile feedback only
      osc.frequency.value = 22;
      osc.connect(gain); gain.connect(ctx.destination);
      osc.start(); osc.stop(ctx.currentTime + 0.012);
    }
  } catch (_) {
    /* never throw from a haptic helper */
  }
}
